// src/pages/Pricing.jsx
import React, { useState } from 'react'
import { useApp } from '../context/AppContext'
import { PLANS, PLAN_ORDER } from '../utils/constants'
import { mockUpgrade } from '../services/billingService'
import { startRazorpayCheckout } from '../services/razorpayService'

const FEATURES = {
  free:       ['10 transactions total', 'Basic reports', 'All account types', 'Excel export'],
  pro:        ['1,000 transactions', 'Basic reports', 'All account types', 'Excel export', 'Priority support'],
  premium:    ['10,000 transactions', 'Full reports & charts', 'All account types', 'Excel export', 'Priority support', 'Budget goals (soon)'],
  enterprise: ['Unlimited transactions', 'Full reports & charts', 'Custom integrations', 'Dedicated support', 'SLA guarantee'],
}

export default function Pricing() {
  const { currentProfile, currentUser, refreshProfile, toast, toastError } = useApp()
  const [upgrading, setUpgrading] = useState(null)
  const [payErr,    setPayErr]    = useState('')
  const currentPlanId = currentProfile?.plan || 'free'

  const handleUpgrade = async (planId) => {
    if (planId === 'enterprise') {
      window.location.href = 'mailto:tecfin@kempu.in?subject=Enterprise%20Plan%20Inquiry'
      return
    }
    if (planId === currentPlanId) return

    setPayErr('')
    setUpgrading(planId)
    const plan = PLANS[planId]

    // ── Launch real Razorpay checkout ─────────────────────────────
    await startRazorpayCheckout({
      planId,
      amountInPaise: plan.price * 100,   // convert ₹ to paise
      planName:      plan.name,
      userId:        currentProfile.id,
      userEmail:     currentUser?.email   || '',
      userName:      currentProfile?.name || '',

      onSuccess: async (result) => {
        // Payment verified server-side — profile already updated in Edge Function
        await refreshProfile()
        toast(`Upgraded to ${plan.name}! Welcome aboard.`)
        setUpgrading(null)
      },

      onFailure: (err) => {
        console.error('Payment failed:', err)
        setPayErr(err.message || 'Payment failed. Please try again.')
        setUpgrading(null)
      },

      onDismiss: () => {
        // User closed the modal — not an error
        setUpgrading(null)
      },
    })
  }

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Choose your plan</h1>
        <p style={{ color: 'var(--gray-600)', fontSize: 14 }}>
          Upgrade anytime. No hidden fees. Secure checkout powered by Razorpay.
        </p>
      </div>

      {/* Payment error banner */}
      {payErr && (
        <div style={{
          background: 'var(--red-50)', border: '1px solid #f0b8b8', borderRadius: 10,
          padding: '10px 16px', marginBottom: 16, fontSize: 13, color: 'var(--red-600)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <i className="ti ti-alert-circle" aria-hidden="true" />
          {payErr}
          <button onClick={() => setPayErr('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red-600)', fontSize: 16 }}>✕</button>
        </div>
      )}

      {/* Plan cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14 }}>
        {PLAN_ORDER.map(planId => {
          const plan      = PLANS[planId]
          const isCurrent = planId === currentPlanId
          const isPopular = planId === 'premium'
          const isBusy    = upgrading === planId

          return (
            <div
              key={planId}
              className="card"
              style={{
                position: 'relative',
                border: isCurrent ? '2px solid var(--brand-400)' : isPopular ? '2px solid var(--green-600)' : '1px solid var(--border)',
                display: 'flex', flexDirection: 'column',
              }}
            >
              {isPopular && !isCurrent && (
                <div style={{ position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)', background: 'var(--green-600)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 12px', borderRadius: 20, letterSpacing: 0.3 }}>
                  MOST POPULAR
                </div>
              )}
              {isCurrent && (
                <div style={{ position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)', background: 'var(--brand-600)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 12px', borderRadius: 20, letterSpacing: 0.3 }}>
                  YOUR PLAN
                </div>
              )}

              <div style={{ marginTop: 6, marginBottom: 14 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--gray-900)' }}>{plan.name}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 3, minHeight: 32 }}>{plan.tagline}</div>
              </div>

              <div style={{ marginBottom: 18 }}>
                <span style={{ fontSize: 28, fontWeight: 700, fontFamily: 'Courier New, monospace' }}>{plan.priceLabel}</span>
                {plan.price !== null && plan.price > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--gray-600)', marginLeft: 5 }}>/ {plan.billing}</span>
                )}
                {plan.price === 0 && (
                  <span style={{ fontSize: 12, color: 'var(--gray-600)', marginLeft: 5 }}>{plan.billing}</span>
                )}
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: 20, flex: 1 }}>
                {FEATURES[planId].map(f => (
                  <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 13, color: 'var(--gray-800)', marginBottom: 9 }}>
                    <i className="ti ti-check" style={{ color: 'var(--green-600)', fontSize: 15, flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                className={`btn ${isCurrent ? '' : isPopular ? 'btn-success' : 'btn-primary'}`}
                disabled={isCurrent || isBusy || !!upgrading}
                onClick={() => handleUpgrade(planId)}
                style={{ width: '100%', justifyContent: 'center', padding: '10px 14px' }}
              >
                {isBusy
                  ? <><i className="ti ti-loader-2" aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }} /> Processing…</>
                  : isCurrent
                    ? <><i className="ti ti-check" aria-hidden="true" /> Current plan</>
                    : planId === 'enterprise'
                      ? <><i className="ti ti-mail" aria-hidden="true" /> Contact TecFin</>
                      : <><i className="ti ti-credit-card" aria-hidden="true" /> Pay with Razorpay</>
                }
              </button>
            </div>
          )
        })}
      </div>

      {/* Trust badges */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 24, flexWrap: 'wrap' }}>
        {[
          { icon: 'ti-shield-check', text: 'Secure checkout' },
          { icon: 'ti-brand-mastercard', text: 'UPI, Cards, Netbanking' },
          { icon: 'ti-lock', text: '256-bit SSL' },
          { icon: 'ti-receipt', text: 'Razorpay protected' },
        ].map(b => (
          <div key={b.text} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--gray-500)' }}>
            <i className={`ti ${b.icon}`} style={{ fontSize: 14 }} aria-hidden="true" />
            {b.text}
          </div>
        ))}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}
